import { IEngineBenefitSelections } from "types/BenefitSelection/BenefitSelection";

/**
 * CLE-2816 — the copay the benefit engine quoted off a payer's PER-PROCEDURE COPAY LIST, for the order pages.
 *
 * Some UHC plans answer a care setting with one Co-Payment line per procedure family
 * (`Message: [ITEM, "AMOUNT VARIES BY LOCATION"]`, a lower 8H and a higher M2 leg). The engine quotes the item the
 * estimate's procedure maps to, or — when none does — the list's generic `OUTPATIENT HOSPITAL CARE` at its lower leg,
 * and says so in `provenance.copayList`. Ani (2026-10-30) asked for both to be visible where estimates are made and
 * viewed: the generic fallback as a yellow note under the copay box (`EngineProvenanceBanner`), and the row the engine
 * used as "Selected" in the benefit tabs.
 *
 * 🚨 GATED ON `quoted`, like the CLE-2951 banner. On an account still quoted by legacy the engine's value may not be
 * the number on screen — never point at a row for a number the patient was not given.
 *
 * 🚨 ONLY A READ OFF THE LIST. `rung: 'AUTHORITY'` + `readFrom: 'LINE'`: a CORRECTING override keeps `copayList` on
 * its provenance (the divergence classifier reads it) but replaces the amount, so neither the note nor the tag is true
 * of it any more.
 *
 * THE ROW COMES FROM THE ENGINE'S `pointer` — computed by the API from the same row origins as the AI's pointers — and
 * NEVER from matching amounts. The row's own text and amount only CONFIRM it: the row must carry the item and the
 * quoted amount, or nothing is tagged (a stored order whose rows no longer line up stays untagged rather than wrong).
 */

/** The stored eligibility response (`benefitDetails.response`), or as much of it as we read. */
export interface IResponseWithCopayList {
  engineBenefitSelections?: IEngineBenefitSelections | null;
  details?: any;
}

export interface IQuotedCopayListPick {
  item: string;
  fallback: boolean;
  value: number | null;
  pointer: unknown;
}

export const quotedCopayListPick = (
  response: IResponseWithCopayList | null | undefined
): IQuotedCopayListPick | null => {
  const selection: any =
    response?.engineBenefitSelections?.benefits?.FLAT_COPAY;
  if (!selection || selection.quoted !== true) return null;
  const { provenance } = selection;
  if (
    !provenance ||
    provenance.rung !== "AUTHORITY" ||
    provenance.readFrom !== "LINE"
  ) {
    return null;
  }
  const list = provenance.copayList;
  if (
    !list ||
    typeof list.item !== "string" ||
    list.item.length === 0 ||
    typeof list.fallback !== "boolean"
  ) {
    return null;
  }
  return {
    item: list.item,
    fallback: list.fallback,
    value: typeof selection.value === "number" ? selection.value : null,
    pointer: selection.pointer,
  };
};

export interface ISelectedCopayRow {
  /** The `details` key of the tab, e.g. `HospitalOutPatientSummary`. */
  tab: string;
  /** The row object itself, from `details[tab].serviceDetails.copay`. */
  row: unknown;
}

/** The copay row the engine's pointer names, confirmed by the row's item and amount — or null. */
export const selectedCopayRow = (
  response: IResponseWithCopayList | null | undefined
): ISelectedCopayRow | null => {
  const pick = quotedCopayListPick(response);
  const pointer: any = pick?.pointer;
  if (
    !pick ||
    !pointer ||
    typeof pointer.tab !== "string" ||
    pointer.row !== "copay" ||
    !Number.isInteger(pointer.index)
  ) {
    return null;
  }
  const rows = response?.details?.[pointer.tab]?.serviceDetails?.copay;
  const row = Array.isArray(rows) ? rows[pointer.index] : undefined;
  if (!row || typeof row !== "object") return null;
  const carriesItem =
    Array.isArray(row.messages) && row.messages.includes(pick.item);
  const amount =
    row.monetaryAmount === null || row.monetaryAmount === undefined
      ? NaN
      : Number(row.monetaryAmount);
  if (!carriesItem || pick.value === null || amount !== pick.value) {
    return null;
  }
  return { tab: pointer.tab, row };
};

/**
 * Marks which of a tab's `additionalCopay` rows is the selected one — BY IDENTITY, so a tab that renders a filtered
 * copy of `serviceDetails.copay` still marks the right row (or none).
 */
export const tagSelectedCopay = <T extends Record<string, any>>(
  items: T[],
  tab: string | undefined,
  selected: ISelectedCopayRow | null
): (T & { selectedCopayIndexes?: number[] })[] => {
  if (!tab || !selected || selected.tab !== tab) return items;
  return items.map((item) => ({
    ...item,
    selectedCopayIndexes: Array.isArray(item.additionalCopay)
      ? item.additionalCopay
          .map((row: unknown, index: number) =>
            row === selected.row ? index : -1
          )
          .filter((index: number) => index >= 0)
      : [],
  }));
};
